"use client";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { useQuery } from "@tanstack/react-query";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import ReviewCard from "@/components/landing/ReviewCard";
import { reviews, brands, stats } from "@/components/landing/dummy";
import {
  getMemberInfo,
  getOngoingCourses,
  getUplineMemberNo,
  getCourseVideoId,
  getCategories,
  MemberCourse,
} from "@/api/query/facilitator";
import { useSearch } from "@/api/hook/useSearch";
import { useCategories } from "@/api/hook/useCategories";
import { SearchResults } from "@/components/landing/CourseSearch";
import { HallOfFamePreview } from "@/components/hall-of-fame/HallOfFamePreview";
import { InfoCenterSection } from "@/components/info-center/info-section";
import { useInView } from "react-intersection-observer";
import CountUp from "react-countup";
import { VideoGallery } from "@/components/VideoGallery";
import VerifyFacilitatorModal from "@/components/VerifyMember";
import FreeCoursesSection from "@/components/landing/FreeCoursesSection";
import { setMemberNo as saveMemberNo } from "@/api/utils/session";

interface FacilitatorClientProps {
  memberNo: string;
}
interface StatItemProps {
  title: string;
  body: string;
}

export default function FacilitatorClient({
  memberNo: initialMemberNo,
}: FacilitatorClientProps) {
  const router = useRouter();
  const [showMore, setShowMore] = useState(false);
  const [memberNo, setMemberNo] = useState<string>(initialMemberNo);
  const [coursesWithVideoIds, setCoursesWithVideoIds] = useState<
    (MemberCourse & { videoId?: string })[]
  >([]);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const searchModalRef = useRef<HTMLDivElement>(null);
  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const {
    searchTerm,
    setSearchTerm,
    searchResults,
    showSearchModal,
    setShowSearchModal,
    isSearching,
  } = useSearch();

  useEffect(() => {
    // Persist the memberNo in a cookie
    if (memberNo) {
      saveMemberNo(memberNo);
    }
  }, [memberNo]);

  useEffect(() => {
    // Check if it's a marketer member number
    if (memberNo && memberNo.startsWith("MKT")) {
      getUplineMemberNo(memberNo)
        .then((uplineMemberNo) => {
          setMemberNo(uplineMemberNo);
        })
        .catch((error) => {
          console.error("Error fetching upline member number:", error);
        });
    }
  }, [memberNo]);

  // Fetch member info
  const { data: facilitatorInfo, isLoading: isLoadingInfo } = useQuery({
    queryKey: ["facilitatorInfo", memberNo],
    queryFn: () => getMemberInfo(memberNo),
    enabled: !!memberNo,
  });

  // Fetch ongoing courses
  const { data: ongoingCourses, isLoading: isLoadingCourses } = useQuery({
    queryKey: ["ongoingCourses", memberNo],
    queryFn: () => getOngoingCourses(memberNo),
    enabled: !!memberNo,
  });

  // Process courses to get video IDs
  useEffect(() => {
    if (ongoingCourses) {
      Promise.all(
        ongoingCourses.map(async (course) => {
          if (course.course.shortVideoEnabled) {
            const videoId = await getCourseVideoId(course.course.id);
            return { ...course, videoId };
          }
          return course;
        }),
      ).then((coursesWithIds) => {
        setCoursesWithVideoIds(coursesWithIds);
      });
    }
  }, [ongoingCourses]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchModalRef.current &&
        !searchModalRef.current.contains(event.target as Node)
      ) {
        setShowSearchModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setShowSearchModal]);

  // Update the hero section search input
  const handleSearch = () => {
    if (searchTerm.length >= 2) {
      setSearchTerm("");
      setShowSearchModal(false);
    }
  };

  const isVolunteer = memberNo.startsWith("VOL");
  const isTrainee = memberNo.startsWith("TRN");
  const isFacilitator = memberNo.startsWith("FAC");
  const isVip = memberNo.startsWith("VIP");

  const roleLabel = isTrainee
    ? "Trainee"
    : isVolunteer
      ? "Volunteer"
      : isFacilitator
        ? "Facilitator"
        : isVip
          ? "Training Provider"
          : "";

  if (isLoadingCourses || isLoadingInfo) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const StatItem = ({ title, body }: StatItemProps) => {
    const { ref, inView } = useInView({
      triggerOnce: false,
      threshold: 0.3,
    });

    // Extract numeric part (remove +, %, etc.)
    const numericValue = parseFloat(title.replace(/[^0-9.]/g, ""));
    const suffix = title.replace(/[0-9.]/g, "");

    return (
      <div ref={ref} className="flex flex-col gap-2 md:gap-3">
        <h3 className="font-bold text-3xl md:text-4xl lg:text-5xl xl:text-6xl">
          {inView ? (
            <CountUp end={numericValue} duration={2} suffix={suffix} />
          ) : (
            title
          )}
        </h3>
        <p className="font-medium text-lg md:text-xl lg:text-2xl">{body}</p>
      </div>
    );
  };

  return (
    <>
      <Header />
      <main className="flex flex-col min-h-screen bg-[#FFF] gap-16 md:gap-28">
        {/* Hero Section */}
        <div className="relative bg-[#21205A] w-full">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-start lg:items-center min-h-[80vh] lg:min-h-[75vh] pt-20 pb-12 lg:py-16">
              {/* Left Content */}
              <div className="w-full lg:w-1/2 flex flex-col gap-4 md:gap-6 z-10 mb-8 lg:mb-0">
                <div className="flex flex-col gap-2 md:gap-4">
                  <h1 className="font-bold text-[#FFF] text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-tight">
                    Learn from the best.
                  </h1>
                  <h1
                    className="font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-tight"
                    style={{
                      backgroundImage:
                        "linear-gradient(90deg, #EFAE33, #B97BFF)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                      display: "inline-block",
                    }}
                  >
                    Learn on your schedule.
                  </h1>
                </div>

                <p className="font-medium text-base sm:text-lg md:text-xl lg:text-2xl text-[#FFF] max-w-2xl">
                  Africa's first synchronous learning platform for world
                  leaders. Join thousands of students from several countries to
                  move ahead educationally without boundaries.
                </p>

                {/* Search Section */}
                <section>
                  <div className="flex flex-col sm:flex-row gap-4 w-full mt-4">
                    <div className="relative flex-1  w-full">
                      <input
                        type="text"
                        name="search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="What do you want to learn?"
                        className="block w-full rounded-md py-3 sm:py-4 border border-black pl-12 pr-4 text-base lg:text-lg font-medium text-[#646464] placeholder:text-[#646464] placeholder:font-medium focus:outline-none focus:ring-2 focus:ring-primary/50"
                        aria-describedby="search-description"
                      />
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <HiMagnifyingGlass className="h-5 w-5 text-primary" />
                      </div>
                      {showSearchModal && (
                        <div ref={searchModalRef}>
                          <SearchResults
                            results={searchResults}
                            isSearching={isSearching}
                            searchTerm={searchTerm}
                            onResultClick={() => {
                              setSearchTerm("");
                              setShowSearchModal(false);
                            }}
                            className="z-50"
                          />
                        </div>
                      )}
                    </div>

                    {/* <button
                           onClick={handleSearch}
                           className="bg-[#8253C3] hover:bg-[#6f45a8] transition-colors rounded-md px-6 py-3 sm:py-4 text-white font-bold text-base lg:text-lg whitespace-nowrap"
                         >
                           Search
                         </button> */}
                  </div>

                  {/* CTA + Verify row */}
                  <div className="flex flex-col sm:flex-row gap-3 w-full mt-2">
                    <a
                      href="https://student.laplagemetaverse.org/sign-up"
                      target="blank"
                      className="flex items-center justify-center gap-2 bg-[#c89a44] hover:opacity-90 transition-opacity rounded-md px-6 py-3 md:py-4 text-[#FFF] font-bold text-base lg:text-base whitespace-nowrap flex-1 md:max-w-[calc(50%-6px)]"
                    >
                      <span>✦</span>
                      First time? Create an account
                    </a>

                    <button
                      onClick={() => setShowVerifyModal(true)}
                      className="bg-[#8253C3] hover:bg-[#6f45a8] transition-colors rounded-md px-6 py-3 md:py-4 text-[#FFF] font-bold text-base lg:text-base whitespace-nowrap flex-1 md:max-w-[calc(50%-6px)]"
                    >
                      Verify Our Facilitator
                    </button>
                  </div>
                </section>
              </div>

              {/* Right Image - Fixed */}
              <div className="lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2 w-full lg:w-1/2">
                <div className="relative w-full aspect-square lg:aspect-[4/3] max-w-2xl mx-auto lg:max-w-none">
                  <Image
                    src="/assets/landing/home/hero.png"
                    alt="lady at the office"
                    fill
                    priority
                    className="object-contain object-center lg:object-right"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 800px"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Facilitator Profile Section */}
        <div className="relative bg-[#21205A] w-full">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center min-h-[50vh] lg:min-h-[40vh] pt-20 pb-12 lg:py-16">
              <div className="text-center w-full lg:w-2/3 mb-8">
                <h1 className="font-bold text-[#FFF] text-3xl sm:text-4xl md:text-5xl mb-4">
                  {roleLabel.toUpperCase()}
                </h1>
                {isLoadingInfo ? (
                  <div className="animate-pulse bg-[#D1D5DB] h-20 w-3/4 mx-auto rounded-md"></div>
                ) : facilitatorInfo ? (
                  <section className="bg-[#FFF]/10 backdrop-blur-sm p-6 rounded-xl">
                    <h2 className="font-bold text-2xl sm:text-3xl md:text-4xl text-[#FFF] mb-4">
                      {facilitatorInfo.fullName}
                    </h2>
                    {facilitatorInfo.verified && (
                      <div className="flex items-center justify-center gap-2">
                        <div className="bg-[#22c55e] rounded-full p-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="white"
                            width="24px"
                            height="24px"
                          >
                            <path d="M0 0h24v24H0z" fill="none" />
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                          </svg>
                        </div>
                        <span className="text-[#4ade80] font-semibold">
                          Verified {roleLabel}
                        </span>
                      </div>
                    )}
                  </section>
                ) : (
                  <div className="text-[#FFF] text-xl">
                    No member information available
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Decorative wave */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1440 100"
              fill="#ffffff"
            >
              <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,100L1360,100C1280,100,1120,100,960,100C800,100,640,100,480,100C320,100,160,100,80,100L0,100Z" />
            </svg>
          </div>
        </div>
        <HallOfFamePreview facId={memberNo} />
        {/* Active Classes Section */}
        <div className="container mx-auto px-4">
          <h2 className="font-bold text-3xl md:text-4xl text-center mb-6 text-primary">
            Active Classes
          </h2>
          {isLoadingCourses ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : ongoingCourses && ongoingCourses.length > 0 ? (
            <Swiper
              slidesPerView={
                reviews.length === 1 ? 1 : reviews.length === 2 ? 2 : 3
              }
              centeredSlides={reviews.length >= 3}
              loop={reviews.length >= 3}
              navigation
              autoplay={{
                delay: 2500,
                disableOnInteraction: false,
                reverseDirection: true, // This makes the swiper move to the right instead of left
              }}
              pagination={{
                clickable: true,
                horizontalClass: "swiper-pagination-horizontal",
                bulletClass: "swiper-pagination-bullet",
                bulletActiveClass: "swiper-pagination-bullet-active",
              }}
              breakpoints={{
                320: {
                  slidesPerView: 1,
                  spaceBetween: 10,
                  centeredSlides: reviews.length >= 2,
                },
                768: {
                  slidesPerView: Math.min(reviews.length, 2),
                  spaceBetween: 30,
                  centeredSlides: reviews.length >= 2,
                },
                1024: {
                  slidesPerView: Math.min(reviews.length, 3),
                  spaceBetween: 50,
                  centeredSlides: reviews.length >= 3,
                },
              }}
              modules={[Pagination, Autoplay, Navigation]}
              className="relative testimonial-swiper px-4 pb-12 w-full mx-auto"
            >
              {ongoingCourses.map((memberCourse) => (
                <SwiperSlide key={memberCourse.course.id} className="py-8">
                  <div className="bg-[#FFF] rounded-lg shadow-md overflow-hidden transition-all hover:shadow-lg h-full flex flex-col">
                    <div className="aspect-video relative overflow-hidden">
                      {memberCourse.course.shortVideoEnabled &&
                      coursesWithVideoIds[memberCourse.course.id] ? (
                        <div className="relative w-full h-full">
                          <Image
                            src={`https://img.youtube.com/vi/${
                              coursesWithVideoIds[memberCourse.course.id]
                            }/hqdefault.jpg`}
                            alt={memberCourse.course.courseName}
                            fill
                            className="object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-[#FFF]/30 backdrop-blur-sm flex items-center justify-center">
                              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="white"
                                  width="24px"
                                  height="24px"
                                >
                                  <path d="M0 0h24v24H0z" fill="none" />
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <Image
                          src={`https://laplage-core.landmarkcooperative.org/public/getCoursePics/${memberCourse.course.id}`}
                          alt={memberCourse.course.courseName}
                          fill
                          className="object-cover transition-transform hover:scale-105"
                        />
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="inline-block px-3 py-1 mb-3 text-sm font-medium bg-purple-100 text-primary rounded-full">
                        {memberCourse.course.courseCategory.categoryName}
                      </div>
                      <h3 className="font-bold text-xl mb-4 line-clamp-1 hover:line-clamp-none transition-all">
                        {memberCourse.course.courseName}
                      </h3>
                      <div className="mt-auto">
                        <a
                          href={memberCourse.whatsappLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-[#077707] hover:bg-[#077707]/80 transition-colors px-4 py-2 text-[#FFF] font-medium rounded-md"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="white"
                          >
                            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                          </svg>
                          Join Class
                        </a>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="text-center py-10 bg-gray-50 rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 mx-auto text-[#9CA3AF]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 2a10 10 0 110 20 10 10 0 010-20z"
                />
              </svg>
              <h3 className="mt-4 text-xl font-medium text-gray-900">
                No active courses
              </h3>
              <p className="mt-2 text-[#6B7280]">
                This facilitator has no active courses at the moment.
              </p>
            </div>
          )}
        </div>
        <div className="container mx-auto px-4 py-12 text-center">
          {/* Heading */}
          <h1 className="text-xl md:text-3xl font-bold text-primary">
            La Plage Meta Verse &amp; The Nigeria Jubilee Fellowship Program
            (NJFP)
          </h1>

          {/* Divider */}
          <div className="w-24 h-1 bg-primary mx-auto my-6 rounded"></div>

          {/* Intro Text */}
          <p className="max-w-5xl mx-auto text-lg text-gray-600 leading-relaxed">
            La Plage Meta Verse (LPMV) is a host the Nigeria Jubilee Fellowship
            Program (NJFP) - a Federal Government of Nigeria and United Nations
            Development Programme (UNDP) initiative that empowers young
            graduates with professional work experience, skills, and mentorship.
          </p>

          {!showMore && (
            <button
              onClick={() => setShowMore(true)}
              className="mt-6 px-6 py-2 bg-[#8253C3] hover:bg-[#6f45a8] transition-colors text-[#FFF] font-medium rounded-md"
            >
              Read More
            </button>
          )}

          {/* Extra Content (shown when Read More is clicked) */}
          {showMore && (
            <div className="mt-4 space-y-4 max-w-4xl mx-auto text-center animate-fadeIn">
              <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                As a global training institution committed to capacity building,
                digital empowerment, and professional development, La Plage Meta
                Verse fully supports NJFP&apos;s vision of preparing
                Nigeria&apos;s youth to compete favorably in national and
                international employment markets.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                This partnership reflects our shared mission to bridge the gap
                between learning and meaningful work opportunities, ensuring
                Nigerian youth can thrive in Africa&apos;s largest economy and
                beyond.
              </p>
            </div>
          )}

          {/* Image */}
          <div className="w-full flex justify-center mt-8">
            <img
              src="/assets/dashboard/njfp.jpg"
              alt="NJFP"
              className=" w-full"
            />
          </div>
        </div>
        {/* Brands Section */}
        <div className="container mx-auto px-4">
          <div className="flex flex-col gap-5">
            <h3 className="font-bold text-2xl md:text-3xl lg:text-4xl text-center">
              Members, Affiliations, and Partners
            </h3>
            <Swiper
              slidesPerView={3}
              centeredSlides={true}
              loop={true}
              navigation
              autoplay={{
                delay: 2500,
                disableOnInteraction: false,
                reverseDirection: true,
              }}
              breakpoints={{
                320: { slidesPerView: 1, spaceBetween: 10 },
                640: { slidesPerView: 2, spaceBetween: 20 },
                768: { slidesPerView: 4, spaceBetween: 30 },
                1024: { slidesPerView: 5, spaceBetween: 40 },
              }}
              modules={[Pagination, Autoplay, Navigation]}
              className="testimonial-swiper px-4 pb-12 w-full"
            >
              {brands.map((data) => (
                <SwiperSlide key={data.id} className="py-14">
                  <div className="flex items-center justify-center my-auto">
                    <Image
                      src={data.image}
                      alt={data.alt}
                      width={data.width}
                      height={data.height}
                      className="max-w-full h-auto"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
        {/* Categories Section */}

        <FreeCoursesSection memberNo={memberNo} />

        <div className="container mx-auto px-4">
          <h3 className="font-bold text-primary text-2xl md:text-3xl lg:text-4xl text-center">
            Course Categories
          </h3>
          <div className="mt-8 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {categoriesLoading ? (
              <div className="text-center col-span-full">
                Loading categories...
              </div>
            ) : (
              categories
                ?.filter(
                  (category) => category.categoryName !== "In progress...",
                )
                .map((category) => (
                  <div
                    key={category.id}
                    className="bg-[#F5F3FE] flex flex-row gap-4 md:gap-6 items-center py-6 md:py-8 px-4 md:px-6 rounded-lg cursor-pointer"
                    onClick={() =>
                      router.push(
                        `https://student.laplagemetaverse.org/courses/${category.id}`,
                      )
                    }
                  >
                    <p className="font-bold text-xl md:text-2xl text-primary">
                      {category.categoryName}
                    </p>
                  </div>
                ))
            )}
          </div>
        </div>

        <div className="bg-[#1C1B4D] text-[#FFF] py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8 text-center text-white">
              {stats.map((item) => (
                <StatItem key={item.id} title={item.title} body={item.body} />
              ))}
            </div>
          </div>
        </div>
        {/* Testimonials Section */}
        <section className="bg-[#F4F6F9] py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="flex flex-col gap-6">
              <h3 className="font-bold text-2xl md:text-3xl lg:text-4xl text-primary text-center">
                Review testimonials from satisfied students
              </h3>
              <Swiper
                slidesPerView={
                  reviews.length === 1 ? 1 : reviews.length === 2 ? 2 : 3
                }
                centeredSlides={reviews.length >= 3}
                loop={reviews.length >= 3}
                navigation
                autoplay={{
                  delay: 2500,
                  disableOnInteraction: false,
                  reverseDirection: true, // This makes the swiper move to the right instead of left
                }}
                pagination={{
                  clickable: true,
                  horizontalClass: "swiper-pagination-horizontal",
                  bulletClass: "swiper-pagination-bullet",
                  bulletActiveClass: "swiper-pagination-bullet-active",
                }}
                breakpoints={{
                  320: {
                    slidesPerView: 1,
                    spaceBetween: 10,
                    centeredSlides: reviews.length >= 2,
                  },
                  768: {
                    slidesPerView: Math.min(reviews.length, 2),
                    spaceBetween: 30,
                    centeredSlides: reviews.length >= 2,
                  },
                  1024: {
                    slidesPerView: Math.min(reviews.length, 3),
                    spaceBetween: 50,
                    centeredSlides: reviews.length >= 3,
                  },
                }}
                modules={[Pagination, Autoplay, Navigation]}
                className="relative testimonial-swiper px-4 pb-12 w-full mx-auto"
              >
                {reviews.map((review) => (
                  <SwiperSlide key={review.id} className="py-14 w-full">
                    <ReviewCard data={review} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h1 className="text-xl md:text-4xl font-bold text-primary mb-4">
                Welcome to La Plage Meta Verse
              </h1>
              <p className="text-lg text-muted-foreground max-w-4xl mx-auto">
                Get to know who we are, explore an overview of our services, and
                watch a showcase of our training programs and courses. Discover
                how La Plage Meta Verse empowers individuals and organizations
                through innovation and learning.
              </p>
            </div>
            <VideoGallery />
          </div>
        </section>
        {/* Stats Section */}

        {/* News Section */}
        <InfoCenterSection facId={memberNo} />
        {/* CTA Section */}
        <div className="container mx-auto px-4 mb-12 md:mb-20">
          <div className="bg-[#1C1B4D] flex flex-col lg:flex-row gap-6 md:gap-10 justify-between items-center rounded-xl px-6 md:px-12 lg:px-20 py-12 md:py-16 lg:py-20">
            <h3 className="font-bold text-3xl md:text-4xl lg:text-5xl text-[#FFF] text-center lg:text-left leading-tight lg:basis-1/2">
              Upgrade your Skills with La Plage Meta Verse.
              <br />
              <span
                style={{
                  backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                Take a course today!
              </span>
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 md:gap-5 w-full lg:w-2/5">
              <button
                onClick={() => router.push("/sign-up")}
                className="bg-[#FFF] rounded-md text-lg md:text-xl font-bold w-full py-4 md:py-5"
              >
                Register Now
              </button>
              <button className="bg-[#8253C3] rounded-md text-lg md:text-xl font-bold text-[#FFF] w-full py-4 md:py-5">
                Welcome to the future
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <VerifyFacilitatorModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
      />
    </>
  );
}
